/**
 * The ground everything else stands on: the gravel apron, the lawns, the
 * kivetys, the field and the pirtti's own boards.
 *
 * Not collision - the floors are drawn, and the player is held up by the
 * simulation's own ground plane. But the camp east of the apron is pitched off
 * the gravel's edge, so where these end is where the tents begin.
 */

import { floor, type FloorSpec } from '../world/Level';
import { T } from './levelDimensions';
import { ANSALANKA, ANSALANKA_LINE_Z } from './levelAnsalanka';
import { LAN_EAST_WALL, LAN_HALL_EXTENSION } from './levelJuhlasali';
import { ETEINEN, WC } from './levelNavetanOvi';
import { CAR } from './levelParkkipaikka';
import { KAYTAVA } from './levelPirtti';
import { TORNI, driveway } from './levelPorukka';

/* ================================================================ floors == */

export const floors: FloorSpec[] = [
  floor('piha', 'grass', -2, -51, 181, 21, 0),

  // Einon pirtti.
  floor('pirtti-huone-lattia', 'pirttiFloor', -0.3, -6.3, 10.3, 4.3, 0.01),
  floor('pirtti-kaytava-lattia', 'pirttiFloor', 10.0, 0.1, 33.3, KAYTAVA.northZ + T, 0.01),

  // Behind the pirtti: the path to the huussi, worn through the grass because
  // it is the one thing back here anybody walks to on purpose.
  driveway('polku-huussille', { x: 10.5, z: -3.4 }, { x: 11.6, z: -5.2 }, 0.8, 'dirt'),

  // The parking lot, wide enough to hold the bays on both sides of the aisle.
  floor('parkkipaikka', 'gravel', 34.0, -19.5, 95.0, 9.0, 0.01),

  // Juhlanavetta.
  floor('navetta-lattia', 'lanFloor', 125.65, -45.45, 155.75, -29.65, 0.01),
  floor('juhlasali-lattia', 'lanFloor', 131.55, -39.5, LAN_EAST_WALL, -30.0, 0.02),
  floor('tanssilattia', 'wood', 145.0 + LAN_HALL_EXTENSION, -39.5, LAN_EAST_WALL, -30.0, 0.03),
  floor('eteinen-matto', 'fabric', 129.3, ETEINEN.backZ + 0.05, 131.1, -29.7, 0.03),
  // The cubicles are tiled, unlike the rest of the barn's floor.
  floor('wc-lattia', 'stone', 126.0, WC.firstZ - WC.width * WC.count, 128.85, WC.firstZ, 0.02),
  floor('keittio-lattia', 'lanFloor', 119.4, -36.9, 126.0, -32.2, 0.01),
  floor('porstua-lattia', 'stone', 128.9, -30.0, 131.2, -27.2, 0.02),

  // Kivetys - the paving. The entrance is immediately to its left.
  floor('kivetys', 'stone', 131.2, -29.8, 139.6, -25.6, 0.02),

  // The decking the palju stands on, in front of the navetta.
  floor('paljun-terassi', 'wood', 114.4, -26.6, 124.0, -18.6, 0.02),

  /* ---- the yard is hiekoitettu, and the grass is what is left over ---- */
  //
  // The place is a working pihapiiri, not a lawn with paths mown into it: one
  // gravelled drive comes out of the lot and opens into the apron the cars turn
  // round on in front of the navetta, and the grass is whatever the gravel does
  // not cover - the verge along the facade, the bank the tornimajoitus stands
  // on past the east gable, and the edges out towards the fields.
  driveway('pihatie', { x: 95.5, z: -13.6 }, { x: 116.0, z: -24.6 }, 9.0),
  floor('navetan-piha', 'gravel', 112.0, -27.6, 148.0, -16.5, 0.01),
  // Across the verge to the porch. Everybody walks it, so it is gravel too.
  floor('polku-ovelle', 'gravel', 126.4, -29.2, 131.2, -25.4, 0.02),

  // Naapurin maatila, on the far side of the fence: the track from the gate
  // down to the lato, and the ploughed field the farmstead stands at the top
  // of. Both are there to say the field is worked land rather than the lawn
  // the level used to let you run across.
  driveway('maatilan-tie', { x: 31.0, z: -19.6 }, { x: 44.0, z: -25.2 }, 3.2, 'dirt'),
  floor('pelto', 'dirt', 4.0, -49.0, 92.0, -32.0, 0.01),
  // Off the far corner of the apron to the tornimajoitus, worn by everybody who
  // is sleeping in it: past the end of the gravel, so it is bare earth the
  // whole way. It runs to the door rather than to the building, which is what
  // a path worn by feet does.
  driveway(
    'polku-tornille',
    { x: 147.4, z: -24.2 },
    { x: TORNI.x, z: TORNI.z + TORNI.radius + 0.4 },
    1.2,
    'dirt',
  ),

  // The line everybody actually runs, worn through the gravel to bare earth.
  // It is also the only hint the ansalanka gives you about where its gap is.
  // Off the lot and up the slant the pallets are laid on, because that is the
  // line: the wear runs under them rather than round them, which is what a
  // strip somebody dropped three pallets onto looks like a week later.
  driveway('kulkuura-1', { x: 95.5, z: -14.2 }, { x: ANSALANKA.x - 0.7, z: ANSALANKA_LINE_Z }, 1.5, 'dirt'),
  // Under the audi and out the far side: the drive was here first and the row
  // is parked across it, so the worn strip runs on under the car.
  floor(
    'kulkuura-2',
    'dirt',
    ANSALANKA.x - 2.2,
    ANSALANKA.audiZ + 0.05,
    ANSALANKA.x + 4.6,
    ANSALANKA.audiZ + CAR.width - 0.05,
    0.02,
  ),
  // Off the audi's tail and straight down the klapiteline's corridor, because
  // that is where the line goes now: the wear does not turn for the veräjä
  // until the tub is alongside it.
  driveway('kulkuura-3', { x: ANSALANKA.x + 4.4, z: ANSALANKA_LINE_Z }, { x: 118.4, z: -18.1 }, 1.5, 'dirt'),
];
